import { useState, useEffect, useMemo, useRef } from 'react';
import { LinkIcon, XMarkIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { StatusDot } from '../ui/Badge';
import { dependencyApi, initiativeApi } from '@/services/api';
import { useAuth } from '@/contexts/AuthContext';
import { useAppStore } from '@/stores/appStore';
import type { Dependency } from '@/types';

interface DependenciesTabProps {
  initiativeId: string;
  onDependencyCountChange?: (count: number) => void;
}

export function DependenciesTab({ initiativeId, onDependencyCountChange }: DependenciesTabProps) {
  const { user } = useAuth();
  const { initiatives, products, quarters, triggerRefresh } = useAppStore();
  const [dependencies, setDependencies] = useState<Dependency[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [adding, setAdding] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);

  const canEdit = user?.role === 'ADMIN' || user?.role === 'EDITOR';

  // Always fetch dependencies from API (store data can be stale)
  const loadDependencies = async () => {
    try {
      const fetched = await initiativeApi.getById(initiativeId);
      if (fetched?.dependencies) {
        const activeDeps = fetched.dependencies.filter((d: any) => d.isActive !== false);
        setDependencies(activeDeps);
        onDependencyCountChange?.(activeDeps.length);
      } else {
        setDependencies([]);
        onDependencyCountChange?.(0);
      }
    } catch {
      setDependencies([]);
      onDependencyCountChange?.(0);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadDependencies();
  }, [initiativeId]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Build searchable initiative options grouped by Product > Sub-product
  const searchResults = useMemo(() => {
    if (!search.trim()) return [];

    const existingDepIds = new Set(dependencies.map(d => d.dependsOnInitiativeId));
    const query = search.toLowerCase();

    const results: {
      group: string;
      items: { id: string; title: string; startQuarter: string | null; endQuarter?: string | null }[];
    }[] = [];

    for (const product of products) {
      for (const sp of product.subProducts || []) {
        const matching = initiatives.filter(init =>
          init.id !== initiativeId &&
          !existingDepIds.has(init.id) &&
          init.subProductId === sp.id &&
          init.title.toLowerCase().includes(query)
        );

        if (matching.length > 0) {
          results.push({
            group: `${product.name} > ${sp.name}`,
            items: matching.map(init => ({
              id: init.id,
              title: init.title,
              startQuarter: init.startQuarter,
              endQuarter: init.endQuarter,
            })),
          });
        }
      }
    }

    return results;
  }, [search, initiatives, products, initiativeId, dependencies]);

  const getQuarterLabel = (key: string) => {
    const q = quarters.find(q => q.key === key);
    return q?.fullLabel || key;
  };

  const handleAdd = async (targetId: string) => {
    setAdding(true);
    try {
      await dependencyApi.add(initiativeId, targetId);
      setSearch('');
      setShowDropdown(false);
      await loadDependencies();
      triggerRefresh();
    } catch (err: any) {
      const msg = err?.message || 'Failed to add dependency';
      alert(msg);
    } finally {
      setAdding(false);
    }
  };

  const handleRemove = async (dep: Dependency) => {
    try {
      await dependencyApi.remove(initiativeId, dep.id);
      await loadDependencies();
      triggerRefresh();
    } catch (err) {
      console.error('Failed to remove dependency:', err);
      alert('Failed to remove dependency');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <div className="text-sm text-gray-500">Loading dependencies...</div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Search input for adding dependencies - editors/admins only */}
      {canEdit && (
        <div className="pb-4 border-b border-gray-100" ref={searchRef}>
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setShowDropdown(e.target.value.trim().length > 0);
              }}
              onFocus={() => search.trim() && setShowDropdown(true)}
              placeholder="Search initiatives to link..."
              disabled={adding}
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white disabled:opacity-50"
            />
          </div>

          {/* Search results dropdown */}
          {showDropdown && search.trim() && (
            <div className="mt-1 max-h-56 overflow-y-auto border border-gray-200 rounded-lg bg-white shadow-lg">
              {searchResults.length === 0 ? (
                <div className="px-3 py-4 text-sm text-gray-500 text-center">
                  No matching initiatives found
                </div>
              ) : (
                searchResults.map((group) => (
                  <div key={group.group}>
                    <div className="px-3 py-1.5 text-xs font-semibold text-gray-500 bg-gray-50 sticky top-0">
                      {group.group}
                    </div>
                    {group.items.map((item) => (
                      <button
                        key={item.id}
                        onClick={() => handleAdd(item.id)}
                        disabled={adding}
                        className="w-full text-left px-3 py-2 hover:bg-blue-50 transition-colors flex items-center gap-2 disabled:opacity-50"
                      >
                        <LinkIcon className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                        <div className="min-w-0 flex-1">
                          <div className="text-sm text-gray-900 truncate">{item.title}</div>
                          <div className="text-xs text-gray-500">
                            {getQuarterLabel(item.startQuarter ?? '')}
                            {item.endQuarter && ` → ${getQuarterLabel(item.endQuarter)}`}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      )}

      {/* Dependencies list */}
      <div className="flex-1 overflow-y-auto py-4 space-y-2">
        {dependencies.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <LinkIcon className="w-8 h-8 text-gray-300 mb-2" />
            <p className="text-sm text-text-secondary">No dependencies</p>
            {canEdit && (
              <p className="text-xs text-gray-400 mt-1">Use the search above to link related initiatives</p>
            )}
          </div>
        ) : (
          dependencies.map((dep) => {
            const depInit = dep.dependsOnInitiative;
            const productName = depInit?.subProduct?.product?.name;
            const subProductName = depInit?.subProduct?.name;
            const statusColor = depInit?.status?.color;
            const statusName = depInit?.status?.name;

            return (
              <div
                key={dep.id}
                className="border border-gray-100 rounded-lg px-3 py-2.5 hover:border-gray-200 transition-colors group"
              >
                <div className="flex items-start gap-2">
                  <LinkIcon className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <a
                      href={`/initiative/${dep.dependsOnInitiativeId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm font-medium text-text-primary leading-snug hover:text-blue-600 hover:underline transition-colors"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {depInit?.title || dep.dependsOnInitiativeId}
                    </a>
                    {productName && subProductName && (
                      <div className="text-xs text-gray-500 mt-0.5">
                        {productName} &gt; {subProductName}
                      </div>
                    )}
                    <div className="flex items-center gap-3 mt-1.5">
                      {statusColor && statusName && (
                        <div className="flex items-center gap-1">
                          <StatusDot color={statusColor} />
                          <span className="text-xs text-gray-500">{statusName}</span>
                        </div>
                      )}
                      {depInit?.startQuarter && (
                        <span className="text-xs text-gray-400">
                          {getQuarterLabel(depInit.startQuarter)}
                          {depInit.endQuarter && ` → ${getQuarterLabel(depInit.endQuarter)}`}
                        </span>
                      )}
                    </div>
                  </div>
                  {canEdit && (
                    <button
                      onClick={() => handleRemove(dep)}
                      className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-red-50 hover:text-red-600 text-gray-400 transition-all flex-shrink-0"
                      title="Remove dependency"
                    >
                      <XMarkIcon className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
